import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { OauthService } from './oauth.service';
import {CDSNode, CDSSystem} from './capd.model';
import { APIURL } from './url.config';
import {delay, retryWhen, scan, map} from 'rxjs/operators';
import { throwError } from "rxjs";
import { SecureTokenService } from './secure-token.service';

/** Service for grabbing information from the CDS Tree. */
@Injectable()
export class CDSTreeService {

  constructor(private http: HttpClient,
              private auth: OauthService) { }

  /** Handles errors */
  static handleError(error: Response) {
    return throwError(error.text() || 'Server error');
  }

  /**
   * This function is called only when the app boots up.
   * It finds the current root ID of the logged-in user.
   *
   * @async
   * @func getRootId
   * @returns {Promise} the root ID, which is also stored in the session browser.
   *                    This ID is used to determine if the user is logged in.
   */
  getRootId() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<any>(APIURL + 'api/user', options)
        .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
          if (errorCount >= 3) {
            throw err;
          }
          return errorCount + 1;
        }, 0)).pipe(delay(1000))))
        .toPromise();
    })
    .catch(CDSTreeService.handleError);
  }

  /**
   * Gets a root node.
   *
   * @async
   * @func getRoot
   * @returns {Promise<CDSNode[]>} the user's root, with their child groups if applicable.
   */
  getRoot() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/cdstree/orgs/' +
        sessionStorage.getItem('rootId') + '/children?depth=0');
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
        .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
          if (errorCount >= 3) {
            throw err;
          }
          return errorCount + 1;
        }, 0)))).pipe(delay(1000))
        .toPromise()
        .catch(CDSTreeService.handleError);
    })
    .catch(CDSTreeService.handleError);
  }

  /**
   * Gets the first-level children of a given node.
   *
   * @async
   * @func getBranch
   * @param {CDSNode} root - the node whose children you are retrieving.
   * @returns {Promise<CDSNode[]>} a list of children as CDSNodes.
   */
  getBranch(root: CDSNode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/orgs/' +
        root.rootId + '/children?depth=1';
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
        .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
          if (errorCount >= 3) {
            throw err;
          }
          return errorCount + 1;
        }, 0)))).pipe(delay(1000))
        .toPromise()
        .catch(CDSTreeService.handleError);
    })
    .catch(CDSTreeService.handleError);
  }

  getBranchById(root: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/orgs/' +
          root + '/children?depth=1';
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0)))).pipe(delay(1000))
          .toPromise()
          .catch(CDSTreeService.handleError);
    })
        .catch(CDSTreeService.handleError);
  }

  async getGroupsFromCDSWebAPI(root: CDSNode) {
    return this.auth.getToken().then(async token => {
      const authorization = 'bearer ' + token;
      var groups;
      var rootOid = root.rootId
      rootOid = rootOid.replace('~', '2.16.840.1.113883.3.21')

      let myHeaders = new HttpHeaders({
        Authorization: authorization,
        "auth": "bearer " + SecureTokenService.getAccessToken()
      });

      await this.http.get(APIURL + 'api/cdsweb/groups/' + rootOid + '/', {responseType: 'text', headers: myHeaders})
          .toPromise()
          .then(res => {
            // response must be parsed as JSON in order to be used
            groups = JSON.parse(res)
          })

      return groups;
    }).catch(error => {
        throw error;
      });
  }

  /**
   * Clears the cache and gets the first-level children of a given node.
   *
   * @async
   * @func refreshBranch
   * @param {CDSNode} root - the node whose children you are retrieving.
   * @returns {Promise<CDSNode[]>} the list of children as CDSNodes.
   */
  refreshBranch(root: CDSNode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/cdstree/orgs/' +
        root.rootId + '/refresh');
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
        .toPromise()
        .then(response => {
          return response;
        })
        .catch(error => {
          return this.getBranch(root)
            .then(fallbackResponse => {
              return fallbackResponse;
            })
            .catch(fallbackError => {
              throw fallbackError;
            });
        });
    })
    .catch(error => {
      return CDSTreeService.handleError(error);
    });
  }

  refreshBranchById(root: string) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/cdstree/orgs/' +
          root + '/refresh');
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
          .toPromise()
          .catch(() => this.getBranchById(root));
    })
        .catch(CDSTreeService.handleError);
  }

  /**
   * Clears the cache and gets the first-level groups of a given node.
   *
   * @async
   * @func refreshGroups
   * @param {CDSNode} root - the node whose groups you are retrieving.
   * @returns {Promise<CDSNode[]>} the list of groups as CDSNodes.
   */
  async refreshGroups(root: CDSNode) {
    return this.auth.getToken().then(async token => {
      const authorization = 'bearer ' + token;
      await authorization

      var groups;
      var rootOid = root.rootId
      rootOid = rootOid.replace('~', '2.16.840.1.113883.3.21')

      let myHeaders = new HttpHeaders({
        Authorization: authorization,
        "auth": "bearer " + SecureTokenService.getAccessToken()
      });

      await this.http.get(APIURL + 'api/cdsweb/groups/' + rootOid + '/', {responseType: 'text', headers: myHeaders})
          .toPromise()
          .then(res => {
            // response must be parsed as JSON in order to be used
            groups = JSON.parse(res)
          })

      return groups
    })
      .catch(error => {
        throw error;
      });
  }

  /**
   * Gets the name of the CDS system type as a string.
   *
   * @async
   * @func getCdsSystem
   * @returns {Promise} CDS system type as a string.
   */
  async getCdsSystem() {
    return this.http.get<CDSSystem>(APIURL + 'api/cdstree/system')
      .toPromise()
      .catch(e => console.log(e))
  }

  /**
   * Gets the name of the CDS system from application.properties as a string.
   *
   * @async
   * @func getCdsSystemName
   * @returns {Promise} CDS system name as a string.
   */
  async getCAPDAdminEnvironmentName() {

    return this.http.get(APIURL + 'api/cdstree/systemname', {responseType: 'text'})
        .toPromise()
        .catch(e => console.log(e))
  }

  getTreeChildren(root: CDSNode) {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = APIURL + 'api/cdstree/build/orgs/' +
          root.rootId + '/children';
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0)))).pipe(delay(1000))
          .toPromise()
          .catch(CDSTreeService.handleError);
    })
        .catch(CDSTreeService.handleError);
  }

  getTreeRoot() {
    return this.auth.getToken().then(token => {

     let userRootOid = token.split('.')[1];
     userRootOid = userRootOid.replace(/-/g, '+').replace(/_/g, '/');
     userRootOid = atob(userRootOid);
     userRootOid = JSON.parse(userRootOid).topOrgRootId;

     if (sessionStorage.getItem('rootId') == null && userRootOid != null) {
      sessionStorage.setItem('rootId', userRootOid);
    } else if (sessionStorage.getItem('rootId') == null && userRootOid == null) {
      sessionStorage.setItem('rootId', '~');
      userRootOid = '~';
    }

      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/cdstree/build/orgs/' +
          userRootOid + '/children');
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<CDSNode[]>(url, options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0)))).pipe(delay(1000))
          .toPromise()
          .catch(CDSTreeService.handleError);
    })
        .catch(CDSTreeService.handleError);
  }

  getTreeRoots() {
    return this.auth.getToken().then(token => {
      const authorization = 'bearer ' + token;
      const url = (APIURL + 'api/cdstree/roots');
      const options = {
        headers: new HttpHeaders({ Authorization: authorization })
      };

      return this.http.get<string>(url, options)
          .pipe(retryWhen(errors => errors.pipe(scan((errorCount, err) => {
            if (errorCount >= 3) {
              throw err;
            }
            return errorCount + 1;
          }, 0)))).pipe(delay(1000))
          .toPromise()
          .catch(CDSTreeService.handleError);
    })
        .catch(CDSTreeService.handleError);
  }
}
